import { computed, ref } from 'vue';
import type { Dict } from '@aviata/platform-types';
import { searchFormCalendar } from 'shared';

import * as lowPricesApi from './api';
import type {
  ExtendedFetchLowPricesParams,
  SearchCalendarMap,
  UseCalendarProp,
} from './types';
import { calendarLib } from './lib';

const initialVisibleItems = 3;

function useCalendar({ defaultMode, fetchLowPrices }: UseCalendarProp) {
  const mode = ref<searchFormCalendar.config.CalendarMode>(
    defaultMode || searchFormCalendar.config.CALENDAR_MODES.SINGLE,
  );

  const visibleItemsNumber = ref(initialVisibleItems);

  const getVisibleItems = () => visibleItemsNumber.value;
  const setVisibleItems = (items: number) => (visibleItemsNumber.value = items);

  const searchCalendarMap = ref<SearchCalendarMap>({
    depToArrResponse: null,
    arrToDepResponse: null,
  });

  const airlines = ref<Record<string, Dict.Airlines.Airline[]>>({});

  const isDirectFligths = computed(() => airlines.value.up_to_0?.length > 0);

  const getAirlines = () => {
    if (isDirectFligths.value) {
      return airlines.value.up_to_0 ?? [];
    }
    return airlines.value.up_to_1 ?? [];
  };

  const clearAirlines = () => (airlines.value = {});

  const moveAirlineToTop = (idx: number) => {
    calendarLib.moveAirlineToTopMutation(getAirlines(), idx);
  };

  const getMode = () => mode.value;
  const setMode = (newMode: searchFormCalendar.config.CalendarMode) =>
    (mode.value = newMode);
  const clearSearchCalendarMap = () =>
    (searchCalendarMap.value = {
      depToArrResponse: null,
      arrToDepResponse: null,
    });

  /**
   * Пересекает списки авиакомпаний для направления "туда" и "обратно",
   * чтобы определить общий список доступных авиакомпаний для фильтрации.
   *
   * Алгоритм работы:
   * 1. Проверяет, есть ли фильтры у обоих направлений (туда и обратно).
   *    Если хотя бы у одного их нет — функция завершает выполнение.
   * 2. Создает пересечение авиакомпаний для обоих направлений с помощью `calendarLib.createIntersectedAirlines`.
   * 3. Если уже есть выбранные авиакомпании (`getAirlines().length > 0`),
   *    то функция выходит, чтобы не перезаписывать их.
   * 4. Заполняет `airlines.value` пересечением авиакомпаний и объединенным справочником авиакомпаний
   *    (`calendarLib.getUnionDictAirlines`).
   */
  function intersectAirlines() {
    if (
      !searchCalendarMap.value.depToArrResponse?.filter ||
      !searchCalendarMap.value.arrToDepResponse?.filter
    ) {
      return;
    }
    const intersectedAirlines = calendarLib.createIntersectedAirlines(
      searchCalendarMap.value.depToArrResponse.filter,
      searchCalendarMap.value.arrToDepResponse.filter,
    );
    if (getAirlines().length > 0) {
      return;
    }
    airlines.value = calendarLib.fillIntersectedAirlines(
      intersectedAirlines,
      calendarLib.getUnionDictAirlines(
        searchCalendarMap.value.depToArrResponse.dict?.airlines || [],
        searchCalendarMap.value.arrToDepResponse.dict?.airlines || [],
      ),
    );
  }

  function getLowPrices({
    dep,
    arr,
    isYouth,
    passengers,
    cabinClass,
    dateFrom,
    dateTo,
    arrToDep = false,
  }: ExtendedFetchLowPricesParams) {
    const { adt, chd, inf, yth } = passengers;
    const getPaxAmount = (amount: number) => (isYouth ? 0 : amount);
    const params = {
      dep_city: dep,
      arr_city: arr,
      adults: getPaxAmount(adt),
      children: getPaxAmount(chd),
      infants: getPaxAmount(inf),
      youth: isYouth ? yth : 0,
      class: cabinClass,
      from: dateFrom,
      to: dateTo,
    };

    return lowPricesApi
      .fetchLowPrices({ fetchLowPrices, params })
      .then(data => {
        if (arrToDep) {
          searchCalendarMap.value.arrToDepResponse = data;
        } else {
          searchCalendarMap.value.depToArrResponse = data;
        }
      });
  }

  return {
    getMode,
    setMode,
    getLowPrices,
    searchCalendarMap,
    clearSearchCalendarMap,
    getVisibleItems,
    setVisibleItems,
    intersectAirlines,
    isDirectFligths,
    moveAirlineToTop,
    getAirlines,
    clearAirlines,
  };
}

export const calendarBase = {
  model: { useCalendar },
};
